import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiTrash2, FiMinus, FiPlus, FiShoppingCart } from 'react-icons/fi';
import PortalHeader from '../../components/Portal/PortalHeader';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../contexts/AuthContext';
import { createPortalOrder, getPortalProfile } from '../../services/portal/portalApi';
import './CrearOrden.scss';

const CrearOrden = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    cartItems,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal
  } = useCart();

  const [formData, setFormData] = useState({
    notes: '',
    delivery_address: ''
  });

  const [clientData, setClientData] = useState(null);
  const [isLoadingClient, setIsLoadingClient] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Cargar información del cliente al montar el componente
  useEffect(() => {
    const loadClientData = async () => {
      if (!user) {
        setIsLoadingClient(false);
        return;
      }

      try {
        const profileData = await getPortalProfile();
        setClientData(profileData);

        // Usar la dirección de entrega ya construida por la API
        const address = profileData.delivery_info?.delivery_address || '';

        setFormData(prev => ({
          ...prev,
          delivery_address: address
        }));
      } catch (err) {
        console.error('Error loading client data:', err);
      } finally {
        setIsLoadingClient(false);
      }
    };

    loadClientData();
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleQuantityChange = (productId, delta) => {
    const item = cartItems.find(i => i.id === productId);
    if (!item) return;

    const newQuantity = item.quantity + delta;
    if (newQuantity > 0) {
      updateQuantity(productId, newQuantity);
    }
  };

  const handleSubmitOrder = async (e) => {
    e.preventDefault();

    if (cartItems.length === 0) {
      setError('El carrito está vacío');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Preparar items para el backend
      const items = cartItems.map(item => ({
        product_id: item.id,
        quantity: item.quantity
      }));

      const orderData = {
        items,
        notes: formData.notes || undefined,
        delivery_address: formData.delivery_address || undefined
      };

      const response = await createPortalOrder(orderData);

      setSuccess(true);
      clearCart();

      // Redirigir al historial de órdenes después de 2 segundos
      setTimeout(() => {
        navigate('/portal/historial');
      }, 2000);

    } catch (err) {
      setError(err.message || 'Error al crear la orden');
      console.error('Error creating order:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getProductImageUrl = (product) => {
    if (product.images && product.images.length > 0) {
      const primaryImage = product.images.find(img => img.is_primary) || product.images[0];
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
      const baseUrl = apiUrl.replace(/\/api\/v1\/?$/, '');
      return `${baseUrl}${primaryImage.medium_url || primaryImage.url}`;
    }
    return product.image_url;
  };

  const getProductPrice = (item) => {
    return item.is_on_sale && item.sale_discount_price
      ? parseFloat(item.sale_discount_price)
      : parseFloat(item.sale_price);
  };

  const getItemTotal = (item) => {
    return getProductPrice(item) * item.quantity;
  };

  const total = getCartTotal();

  const formatPrice = (value) => {
    const num = Number(value);
    return num % 1 === 0 ? num.toFixed(0) : num.toFixed(2);
  };

  if (success) {
    return (
      <div className="portal-page crear-orden">
        <PortalHeader />
        <div className="portal-page__container">
          <div className="success-screen">
            <div className="success-icon">✓</div>
            <h1>Orden Creada Exitosamente</h1>
            <p>Tu orden ha sido enviada y está pendiente de aprobación.</p>
            <p>Serás redirigido al historial de órdenes...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="portal-page crear-orden">
      <PortalHeader />

      <div className="portal-page__container">
        <div className="crear-orden__header">
          <h1 className="portal-page__title">
            <FiShoppingCart /> Crear Orden
          </h1>
          <button
            className="back-btn"
            onClick={() => navigate('/portal')}
          >
            ← Volver al Portal
          </button>
        </div>

        {cartItems.length === 0 ? (
          <div className="empty-cart">
            <div className="empty-cart__icon">🛒</div>
            <h2>Tu carrito está vacío</h2>
            <p>Agrega productos para crear una orden</p>
            <button
              className="continue-shopping-btn"
              onClick={() => navigate('/portal')}
            >
              Ir a comprar
            </button>
          </div>
        ) : (
          <div className="crear-orden__content">
            {/* Lista de productos en el carrito */}
            <div className="cart-section">
              <h2 className="section-title">Productos en tu carrito ({cartItems.length})</h2>

              <div className="cart-items">
                {cartItems.map((item) => (
                  <div key={item.id} className="cart-item">
                    <div className="cart-item__image">
                      {getProductImageUrl(item) ? (
                        <img src={getProductImageUrl(item)} alt={item.name} />
                      ) : (
                        <div className="image-placeholder">Sin imagen</div>
                      )}
                    </div>

                    <div className="cart-item__info">
                      <h3>{item.name}</h3>
                      <div className="price-info">
                        <span className="price">${formatPrice(getProductPrice(item))}</span>
                        {item.is_on_sale && item.sale_discount_price && (
                          <span className="original-price">${formatPrice(parseFloat(item.sale_price))}</span>
                        )}
                      </div>
                    </div>

                    <div className="cart-item__quantity">
                      <button
                        className="qty-btn"
                        onClick={() => handleQuantityChange(item.id, -1)}
                        disabled={item.quantity <= 1}
                      >
                        <FiMinus />
                      </button>
                      <span className="qty-display">{item.quantity}</span>
                      <button
                        className="qty-btn"
                        onClick={() => handleQuantityChange(item.id, 1)}
                      >
                        <FiPlus />
                      </button>
                    </div>

                    <div className="cart-item__total">
                      <span className="total-label">Subtotal:</span>
                      <span className="total-amount">${formatPrice(getItemTotal(item))}</span>
                    </div>

                    <button
                      className="cart-item__remove"
                      onClick={() => removeFromCart(item.id)}
                      title="Eliminar producto"
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Formulario y resumen */}
            <div className="order-section">
              <div className="order-form-card">
                <h2 className="section-title">Información de entrega</h2>

                <form onSubmit={handleSubmitOrder}>
                  <div className="form-group">
                    <label htmlFor="delivery_address">
                      Dirección de entrega {isLoadingClient && '(cargando...)'}
                    </label>
                    {isLoadingClient ? (
                      <div style={{ padding: '12px', background: '#f3f4f6', borderRadius: '6px', color: '#6b7280' }}>
                        Cargando dirección...
                      </div>
                    ) : formData.delivery_address ? (
                      <div style={{
                        padding: '12px',
                        background: '#f0fdf4',
                        border: '1px solid #86efac',
                        borderRadius: '6px',
                        color: '#166534',
                        lineHeight: '1.5'
                      }}>
                        {formData.delivery_address}
                      </div>
                    ) : (
                      <div style={{ padding: '12px', background: '#fef3c7', borderRadius: '6px', color: '#92400e' }}>
                        No hay dirección de entrega registrada para tu cuenta
                      </div>
                    )}
                    <small style={{ display: 'block', marginTop: '6px', color: '#6b7280', fontSize: '0.85em' }}>
                      Esta es tu dirección de entrega registrada. Contacta con ventas si necesitas cambiarla.
                    </small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="notes">
                      Notas adicionales
                    </label>
                    <textarea
                      id="notes"
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      placeholder="Instrucciones especiales, comentarios, etc. (opcional)"
                      rows={4}
                    />
                  </div>

                  <div className="order-summary">
                    <h3>Resumen de la orden</h3>
                    <div className="summary-row">
                      <span>Productos:</span>
                      <span>{cartItems.length}</span>
                    </div>
                    <div className="summary-row">
                      <span>Unidades totales:</span>
                      <span>{cartItems.reduce((sum, item) => sum + item.quantity, 0)}</span>
                    </div>
                    <div className="summary-row total">
                      <span>Total:</span>
                      <span>${formatPrice(total)}</span>
                    </div>
                  </div>

                  {error && (
                    <div className="error-message">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    className="submit-order-btn"
                    disabled={isSubmitting || cartItems.length === 0}
                  >
                    {isSubmitting ? 'Creando orden...' : 'Crear Orden'}
                  </button>

                  <p className="info-note">
                    Tu orden será enviada al equipo de ventas para su revisión y aprobación.
                  </p>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CrearOrden;
